import { Button, IconButton, Stack } from '@mui/material';
import { ChangeEventHandler, MouseEventHandler, useCallback, useRef, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { SelectMenu, type SelectMenuProps } from 'src/components/SelectMenu';
import { FilterResultsTextField } from './filter-results-text-field';

const addButtonSx = {
  width: 'fit-content',
  alignSelf: 'end',
};

export const AutocompleteSearchBar = ({
  addButtonText,
  addButtonTestId,
  onAddButtonClick,
  addMenu,
  hideSearchBar,
  handleOnFilterResultsChange,
  disabled = false,
}: {
  addButtonText: string;
  addButtonTestId?: string;
  onAddButtonClick?: MouseEventHandler<HTMLButtonElement>;
  /** Makes the add button open this menu instead of running `onAddButtonClick`. */
  addMenu?: Omit<SelectMenuProps, 'renderTrigger' | 'disabled'>;
  hideSearchBar?: boolean;
  disabled?: boolean;
  handleOnFilterResultsChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onFilterFieldBlur = useCallback(() => {
    // don't collapse field if it has value
    setIsSearchOpen(!!inputRef.current?.value);
  }, []);

  return (
    <Stack direction="row" alignItems="start" justifyContent="space-between">
      <FilterResultsTextField
        inputRef={inputRef}
        handleOnChange={handleOnFilterResultsChange}
        isTextFieldOpen={isSearchOpen}
        onBlur={onFilterFieldBlur}
      />
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        width={isSearchOpen ? 0 : '100%'}
        overflow="hidden"
        justifyContent="space-between"
        sx={{
          transition: '0.2s width',
          order: hideSearchBar ? 2 : undefined,
        }}
      >
        {!hideSearchBar && (
          <Tooltip title="Open search bar">
            <IconButton
              onClick={() => {
                setIsSearchOpen((currentValue) => !currentValue);
                inputRef.current?.focus();
              }}
            >
              <DynamicIcon name="search" filled />
            </IconButton>
          </Tooltip>
        )}
        {addMenu ? (
          <SelectMenu
            {...addMenu}
            disabled={disabled}
            renderTrigger={({ getTriggerProps }) => (
              <Button {...getTriggerProps()} data-testid={addButtonTestId} sx={addButtonSx}>
                {addButtonText}
              </Button>
            )}
          />
        ) : (
          <Button
            disabled={disabled}
            data-testid={addButtonTestId}
            onClick={onAddButtonClick}
            sx={addButtonSx}
          >
            {addButtonText}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
